import React from 'react';
import {
  Box, Flex, Text, useColorModeValue,
} from '@chakra-ui/react';
import { useFormState } from 'react-final-form';
import { FiRadio, FiAlertTriangle } from 'react-icons/fi';
import { useRnwyFleet, rnwyHealth, rnwyForProject } from '../../hooks/useRnwyStatus';
import { parseVenues } from './rnwyVenues';

// item 861 (Chad/Ryan) — the LIVE capacity strip inside the RNWY venue builder: when this
// proposal's project is linked to a deployed RNWY system, show what the box is running and
// how much headroom it has — and compare it against the venues being QUOTED right now. A
// quote that exceeds the open slots gets a loud red banner (that's a capacity-expansion
// sale, not a surprise at install). Renders nothing for unlinked projects.
const RnwyCapacityStrip: React.FC = () => {
  const { values } = useFormState({ subscription: { values: true } });
  const { data: fleet } = useRnwyFleet();
  const okBg = useColorModeValue('blackAlpha.50', 'whiteAlpha.100');
  const edge = useColorModeValue('blackAlpha.200', 'whiteAlpha.300'); // item 958 — light-mode contrast (whiteAlpha edge vanished on light)

  const v = values as { projectId?: string; rnwyVenuesJson?: string | null };
  // item 1124 — a project can run several processors: sums across the linked boxes
  const row = rnwyForProject(fleet, v.projectId);
  if (!row) return null;
  const systemsLabel = row.systems.join('” + “');

  if (!row.ok) {
    return (
      <Flex align="center" borderWidth="1px" borderColor={edge} borderRadius="lg" px={4} py={2.5} mb={4} opacity={0.7}>
        <Box as={FiRadio} mr={3} />
        <Text fontSize="sm">{`Linked RNWY system${row.systems.length > 1 ? 's' : ''} “${systemsLabel}” — live status unavailable right now.`}</Text>
      </Flex>
    );
  }

  const quoted = parseVenues(v.rnwyVenuesJson ?? undefined).venues.length;
  const exceeds = quoted > row.venueCapacity;
  const health = rnwyHealth(row.venueCapacity);
  const accent = { green: 'green.400', amber: 'orange.300', red: 'red.400' }[health];

  if (exceeds) {
    return (
      <Flex align="center" borderWidth="2px" borderColor="red.400" bg="rgba(229,62,62,0.10)" borderRadius="lg" px={4} py={3} mb={4}>
        <Box as={FiAlertTriangle} color="red.400" fontSize="20px" mr={3} flexShrink={0} />
        <Text fontSize="sm" fontWeight="semibold" color="red.300">
          {`You're quoting ${quoted} venue${quoted === 1 ? '' : 's'}, but “${systemsLabel}” only ${row.systems.length > 1 ? 'have' : 'has'} ${row.venueCapacity} open slot${row.venueCapacity === 1 ? '' : 's'} `}
          {`(running ${row.venuesRunning} of ${row.venueTotalCapacity}). This quote needs a capacity expansion.`}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex align="center" borderWidth="1px" borderColor={edge} bg={okBg} borderRadius="lg" px={4} py={2.5} mb={4}>
      <Box w="8px" h="8px" borderRadius="full" bg={accent} mr={3} flexShrink={0} />
      <Text fontSize="sm">
        <Text as="span" fontWeight="bold">{`LIVE SYSTEM${row.systems.length > 1 ? 'S' : ''} — ${row.systems.join(' + ')}: `}</Text>
        {`${row.venuesRunning} of ${row.venueTotalCapacity} venues running · ${row.venueCapacity} open`}
        {quoted > 0 ? ` · this quote adds ${quoted}` : ''}
      </Text>
    </Flex>
  );
};

export default RnwyCapacityStrip;
